import { useCallback, useEffect, useState } from 'react';
import {
  createSession,
  endSession,
  endSessionUnresolved,
  MediatorServiceError,
  startSession,
  type Session,
} from '../api/mediatorService';

type State =
  | { status: 'idle'; session: Session | null }
  | { status: 'working' }
  | { status: 'error'; error: string; session: Session | null };

export interface SessionApi {
  state: State;
  /** Creates a session if none exists yet for (caseId, meetingId) pair. */
  ensureSession: () => Promise<Session>;
  /** Starts the session; transcription is authorized server-side. */
  start: () => Promise<Session>;
  /** Tells the DO to tear down. */
  end: () => Promise<void>;
  endUnresolved: (reason?: string) => Promise<void>;
}

export function useSession(params: {
  caseId: string;
  meetingId: string;
  initialSession: Session | null;
  accessToken: string;
  userId: string | null;
}): SessionApi {
  const [state, setState] = useState<State>({
    status: 'idle',
    session: params.initialSession,
  });

  // Keep state.session in sync if the initial session changes (e.g. parent
  // re-fetched after a page reload mid-session).
  useEffect(() => {
    setState((prev) =>
      prev.status === 'idle' ? { status: 'idle', session: params.initialSession } : prev,
    );
  }, [params.initialSession]);

  const currentSession = (): Session | null =>
    state.status === 'idle' || state.status === 'error' ? state.session : null;

  const runMutation = useCallback(
    async <T,>(op: () => Promise<T>, onResult: (result: T) => Session | null) => {
      setState({ status: 'working' });
      try {
        const result = await op();
        const next = onResult(result);
        setState({ status: 'idle', session: next });
        return result;
      } catch (err) {
        const message =
          err instanceof MediatorServiceError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Session operation failed';
        setState({ status: 'error', error: message, session: currentSession() });
        throw err;
      }
    },
    // currentSession reads state; we recreate on every render intentionally.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const ensureSession = useCallback(async () => {
    const existing = currentSession();
    if (existing) return existing;
    return runMutation(
      () =>
        createSession(
          {
            case_id: params.caseId,
            zoom_meeting_id: params.meetingId,
            mediator_zoom_user_id: params.userId ?? undefined,
          },
          params.accessToken,
        ),
      (s) => s,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.caseId, params.meetingId, params.userId, params.accessToken]);

  const start = useCallback(async () => {
    const existing = await ensureSession();
    return runMutation(
      () => startSession(existing.id, params.accessToken),
      (s) => s,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ensureSession, params.accessToken]);

  const end = useCallback(async () => {
    const existing = currentSession();
    if (!existing) return;
    await runMutation(
      () => endSession(existing.id, params.accessToken),
      () => ({ ...existing, status: 'ended', ended_at: new Date().toISOString() }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.accessToken]);

  const endUnresolved = useCallback(async (reason?: string) => {
    const existing = currentSession();
    if (!existing) return;
    await runMutation(
      () => endSessionUnresolved(existing.id, params.accessToken, reason),
      () => ({ ...existing, status: 'ended', ended_at: new Date().toISOString() }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.accessToken]);

  return { state, ensureSession, start, end, endUnresolved };
}
